import React, { useEffect, useState, useRef } from "react";
import { CustomIcon, SearchIcon } from "../../utils/CustomIcon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar, faList, faTasks, faEye, faEdit, faPlus } from "@fortawesome/free-solid-svg-icons";
import "./styles/event.css";
import { ProgressBar, Table } from "react-bootstrap";
import tImg from "../assets/t1.jpg";
import { Link, useNavigate } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { eventsAPI } from "../utils/API";
import { extractMonthAndDate, formatDateFull } from "./utils/utils";
import useAuthUserId from "auth/hooks/useAuthUser";
import ContextMenu from "./ContextMenu";
import EventTextIcon from "./common/EventTextIcon";

type ValuePiece = Date | null;

type Value = ValuePiece | [ValuePiece, ValuePiece];

const daysUntilEvent = (eventDateTime: string): number => {
  const eventDate = new Date(eventDateTime);
  const currentDate = new Date();

  // Calculate the difference in milliseconds
  const diffTime = eventDate.getTime() - currentDate.getTime();

  // Convert milliseconds to days
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

const EventTable = () => {
  const navigate = useNavigate();
  //Currently logged in user
  const userId = useAuthUserId();
  // for search
  const [searchQuery, setSearchQuery] = useState("");
  // for more action
  const [openAction, setOpenDropdown] = useState<number | null>(null);

  // for filter
  const [selectedFilterValue, setSelectedFilterValue] = useState<string>("0");

  // calender state
  const [calenderOpen, setCalenderOpen] = useState<boolean>(false);
  const [value, onChange] = useState<Value>(new Date());

  // Event handler to update the selected value
  const handleFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const filterStatus = event.target.value;
    setSelectedFilterValue(filterStatus);
    const filtered = events.filter(event => 
      event.status.toLowerCase() === filterStatus || filterStatus === "all"
    );
    setFilteredEvents(filtered);
  };

  const [events, setEvents] = useState<Record<string, any>[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Record<string, any>[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [warningMessage, setWarningMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { getEventsByUser, getEventTaskCompletionRates } = eventsAPI;

  // Default completion data
  const defaultCompletion = {
    eventId: "",
    total: 0,
    completed: 0,
    completionRate: "0.00%"
  };

  useEffect(() => {
    if (searchQuery) {
      const lowercasedQuery = searchQuery.toLowerCase();
      const filtered = events.filter(event => 
        event.title.toLowerCase().includes(lowercasedQuery) || 
        event.description.toLowerCase().includes(lowercasedQuery)
      );
      setFilteredEvents(filtered);
    } else {
      setFilteredEvents(events); // Reset to all events if searchQuery is empty
    }
  }, [searchQuery, events]);

  const handleFetchEvent = async (userId: string) => {
    try {
      setIsLoading(true);
      const eventListResponse = await getEventsByUser(userId);
      const completionResponse = await getEventTaskCompletionRates(userId);

      if(eventListResponse.events && completionResponse){
        // Merge completion data with event data
        const eventsWithCompletion = eventListResponse.events.map((event: { _id: Event; }) => {
          const completion = completionResponse.find((comp: { eventId: any; }) => comp.eventId === event._id) || { ...defaultCompletion, eventId: event._id };
          return {
            ...event,
            ...completion
          };
        }); 
        setEvents(eventsWithCompletion);
        setFilteredEvents(eventsWithCompletion);
      }
      else{
        setEvents([]);
        setWarningMessage("You currently have no events listed, create an event to start.");
      }
      setIsLoading(false);
    } catch (error) {
      setErrorMessage(`Failed to get events for this user. This user either has no events or fetching user events resulted in an unknown error.`);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if(userId){
      handleFetchEvent(userId);
    }
    else{
      setIsLoading(false);
    }
  }, [userId]);

  // console.log("events", events);

  // handler for list button
  const handleList = () => {};

  // navigate create event page
  const handleCreateEvent = () => {
    navigate("/create-event");
  };

  // onchange  calender
  const handleCalendarChange = (value: Value, event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    onChange(value);
    setCalenderOpen(false);
  };

  const progress = {
    total: 100,
    completed: 10,
  };

  return (
    <div>
      <div className="row g-3 align-items-center">
        {/* Search + actions */}
        <div className="col-12 col-lg-8">
          <div className="d-flex flex-wrap align-items-stretch gap-2">

            {/* Search (input-group avoids absolute positioning issues) */}
            <div className="position-relative flex-grow-1">
              <span
                className="position-absolute top-50 start-0 translate-middle-y ps-3 text-muted"
                style={{ pointerEvents: "none" }}
              >
                <SearchIcon />
              </span>
              <input
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                type="text"
                className="form-control ps-5"
                id="search"
                placeholder="Search events"
              />
            </div>

            {/* Buttons (wrap on small screens) */}
            <div className="d-flex flex-wrap gap-2">
              <button className="btn"
                      onClick={handleList}>
                <span className="me-2">
                  <FontAwesomeIcon icon={faList} />
                </span>
                <span className="d-none d-sm-inline">List of</span> ({filteredEvents.length})
              </button>

              {/* Calendar toggle (visible button on all sizes) */}
              <button className="btn btn-light d-none"
                      onClick={() => setCalenderOpen(true)}>
                <span className="me-2">
                  <FontAwesomeIcon icon={faCalendar} />
                </span>
                <span className="d-none d-sm-inline">Calendar</span>
              </button>
            </div>

            {/* Calendar popover (stack full width on mobile) */}
            {calenderOpen && (
              <div className="w-100">
                <Calendar className="calendar" onChange={handleCalendarChange} value={value} />
              </div>
            )}
          </div>
        </div>

        {/* Filter */}
        <div className="col-12 col-md-6 col-lg-4 d-none">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <div className="filter-label">Filter by:</div>
            <select
              className="form-select w-auto"
              aria-label="filter"
              value={selectedFilterValue}
              onChange={handleFilterChange}
            >
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
        </div>

        {/* Create button */}
        <div className="col-12 col-lg-4">
          <div className="d-grid d-lg-flex justify-content-lg-end">
            <button onClick={handleCreateEvent} className="btn create-btn w-100 w-lg-auto">
              <FontAwesomeIcon icon={faPlus} /> <span className="ms-1 fw-bold">Create event</span>
            </button>
          </div>
        </div>
      </div>

      {/* Event table / states */}
      {!isLoading && !events.length && errorMessage && (
        <div className="error-banner mt-4">
          <div className="error-icon">⚠️</div>
          <div className="error-content">
            <p className="error-title">Something went wrong</p>
            <p className="error-message">{errorMessage}</p>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="d-flex justify-content-center w-100 py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}


      {!isLoading && !errorMessage && (
        <>
          <div className='my-5'>
            <Table className='event-table' responsive>
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Actions</th>
                  <th>Est. Attendees</th>
                  <th>Location Type</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map((event) => (
                  <tr key={event._id}>
                    <td>
                      <div className='d-flex align-items-center'>
                        <div className=''>
                          <p className='mb-0 text-or'>{extractMonthAndDate(event.endDate, "Month")}</p>
                          <p className='mb-0 text-muted fw-bold'>
                            {extractMonthAndDate(event.endDate, "Date")}
                          </p>
                        </div>
                        <div className='d-flex align-items-center '>
                          <div className='mx-2'>
                            {/*<img className='event-Img' src={tImg} alt='Event' />*/}
                             <EventTextIcon title={event.title || 'Unknown'} size={60} />
                          </div>
                          <div>
                            <h6>{event.title}</h6>
                            <p className='mb-1 text-muted fs-12'>{event.summary}</p>
                            <p className='mb-0 text-muted fs-8'>{formatDateFull(event.endDate)}</p>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <p className='mb-0 text-muted '>
                          {event.tasksCompleted}/{event.tasksTotal}
                        </p>
                        <ProgressBar now={event.tasksCompletionRate} />
                      </div>
                    </td>
                    <td>
                      <p className='mb-0 text-muted'> {event.attendee_estimate} </p>
                    </td>
                    <td>
                      {" "}
                      <p className='mb-0 text-muted'>{event.locationType}</p>
                    </td>
                    <td>
                      {/* action */}

                      <div className='position-relative'>
                        <div
                          className='pl-3 cursor-pointer'
                          onClick={() => {
                            if (openAction !== event._id) {
                              setOpenDropdown(event._id);
                            } else {
                              setOpenDropdown(null);
                            }
                          }}
                        >
                          <span style={{ cursor: "pointer" }}>
                            <CustomIcon />
                          </span>
                        </div>
                        {openAction == event._id && <ContextMenu eventId={event._id} />}
                      </div>
                    </td>
                  </tr>
                ))}

                {/* map table data */}
              </tbody>
            </Table>
            {!isLoading && !events.length && warningMessage && (
              <div className='mb-1 ml-4'>
                <p className='text-warning'>{warningMessage}</p> ...
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default EventTable;
